import type {
  QdsTypographyFontFamily,
  QdsTypographyStyle
} from '../../types/blocks.js';

const FONT_FAMILY_CSS: Record<QdsTypographyFontFamily, string> = {
  'ibm-plex-sans': "var(--qds-font-family, var(--theme-font-family, 'IBM Plex Sans', sans-serif))",
  'eb-garamond': "var(--qds-font-family-serif, var(--theme-font-family-serif, 'EB Garamond', serif))",
  'roboto': "var(--qds-font-family-roboto, 'Roboto', sans-serif)",
  'open-sans': "var(--qds-font-family-open-sans, 'Open Sans', sans-serif)"
};

const FONT_FAMILY_LABELS: Record<QdsTypographyFontFamily, string> = {
  'ibm-plex-sans': 'IBM Plex Sans',
  'eb-garamond': 'EB Garamond',
  'roboto': 'Roboto',
  'open-sans': 'Open Sans'
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function toTypographyColor(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function summarizeTypographyColor(value: string): string {
  const variableMatch = value.match(/^var\(\s*(--[A-Za-z0-9-_]+)/);
  if (variableMatch?.[1]) {
    return variableMatch[1];
  }

  return value;
}

export function getTypographyFontFamilyLabel(
  value: QdsTypographyFontFamily | undefined
): string | undefined {
  return value ? FONT_FAMILY_LABELS[value] : undefined;
}

export function getTypographyFontFamilyCss(
  value: QdsTypographyFontFamily | undefined
): string | undefined {
  return value ? FONT_FAMILY_CSS[value] : undefined;
}

export function normalizeTypographyStyle(
  value: unknown
): QdsTypographyStyle | undefined {
  if (!isPlainObject(value)) {
    return undefined;
  }

  const normalized: QdsTypographyStyle = {};

  if (
    value.fontFamily === 'ibm-plex-sans'
    || value.fontFamily === 'eb-garamond'
    || value.fontFamily === 'roboto'
    || value.fontFamily === 'open-sans'
  ) {
    normalized.fontFamily = value.fontFamily;
  }

  const fontSize = toFiniteNumber(value.fontSize);
  if (typeof fontSize === 'number' && fontSize >= 8 && fontSize <= 160) {
    normalized.fontSize = Math.round(fontSize);
  }

  const color = toTypographyColor(value.color);
  if (color) {
    normalized.color = color;
  }

  if (value.italic === true) {
    normalized.italic = true;
  }

  if (value.bold === true) {
    normalized.bold = true;
  }

  if (value.underline === true) {
    normalized.underline = true;
  }

  if (value.strikethrough === true) {
    normalized.strikethrough = true;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function getTypographySummary(value: unknown): string {
  const typography = normalizeTypographyStyle(value);
  if (!typography) {
    return 'Default';
  }

  const parts: string[] = [];
  const fontFamily = getTypographyFontFamilyLabel(typography.fontFamily);
  if (fontFamily) {
    parts.push(fontFamily);
  }

  if (typeof typography.fontSize === 'number') {
    parts.push(`${typography.fontSize}px`);
  }

  if (typography.bold) {
    parts.push('bold');
  }

  if (typography.italic) {
    parts.push('italic');
  }

  if (typography.underline) {
    parts.push('underline');
  }

  if (typography.strikethrough) {
    parts.push('strikethrough');
  }

  if (typography.color) {
    parts.push(`color ${summarizeTypographyColor(typography.color)}`);
  }

  return parts.length > 0 ? parts.join(', ') : 'Default';
}

export function applyTypographyStyle(
  element: HTMLElement | null | undefined,
  value: unknown
): void {
  if (!element) {
    return;
  }

  element.style.removeProperty('font-family');
  element.style.removeProperty('font-size');
  element.style.removeProperty('font-style');
  element.style.removeProperty('font-weight');
  element.style.removeProperty('text-decoration-line');
  element.style.removeProperty('color');

  const typography = normalizeTypographyStyle(value);
  if (!typography) {
    return;
  }

  const fontFamily = getTypographyFontFamilyCss(typography.fontFamily);
  if (fontFamily) {
    element.style.setProperty('font-family', fontFamily);
  }

  if (typeof typography.fontSize === 'number') {
    element.style.setProperty('font-size', `${typography.fontSize}px`);
  }

  if (typography.italic) {
    element.style.setProperty('font-style', 'italic');
  }

  if (typography.bold) {
    element.style.setProperty('font-weight', '600');
  }

  const textDecorations = [
    typography.underline ? 'underline' : '',
    typography.strikethrough ? 'line-through' : ''
  ].filter(Boolean);

  if (textDecorations.length > 0) {
    element.style.setProperty('text-decoration-line', textDecorations.join(' '));
  }

  if (typography.color) {
    element.style.setProperty('color', typography.color);
  }
}
